import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AuditEventType } from '@/types';

interface AuditLogFiltersProps {
  eventType: AuditEventType | 'all';
  userName: string;
  onEventTypeChange: (eventType: AuditEventType | 'all') => void;
  onUserNameChange: (userName: string) => void;
  onClear: () => void;
}

const EVENT_TYPE_OPTIONS: { value: AuditEventType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Events' },
  { value: 'MigrationStarted', label: 'Migration Started' },
  { value: 'MigrationCompleted', label: 'Migration Completed' },
  { value: 'MigrationFailed', label: 'Migration Failed' },
  { value: 'MigrationCancelled', label: 'Migration Cancelled' },
  { value: 'MigrationRetried', label: 'Migration Retried' },
  { value: 'ModuleStarted', label: 'Module Started' },
  { value: 'ModuleCompleted', label: 'Module Completed' },
  { value: 'ModuleFailed', label: 'Module Failed' },
  { value: 'ModuleRetried', label: 'Module Retried' },
  { value: 'ConfigurationChanged', label: 'Config Changed' },
  { value: 'UserLoggedIn', label: 'User Login' },
  { value: 'UserLoggedOut', label: 'User Logout' },
  { value: 'BridgeLookupPerformed', label: 'Bridge Lookup' },
  { value: 'BridgeDuplicateDetected', label: 'Bridge Duplicate' },
  { value: 'MigrationOrganizationAttached', label: 'Organization Attached' },
];

export function AuditLogFilters({
  eventType,
  userName,
  onEventTypeChange,
  onUserNameChange,
  onClear,
}: AuditLogFiltersProps) {
  const hasFilters = eventType !== 'all' || userName.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter by user name..."
          value={userName}
          onChange={(e) => onUserNameChange(e.target.value)}
          className="pl-9"
        />
      </div>

      <Select value={eventType} onValueChange={(v) => onEventTypeChange(v as AuditEventType | 'all')}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Event type" />
        </SelectTrigger>
        <SelectContent>
          {EVENT_TYPE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={onClear}>
          <X className="mr-1 h-4 w-4" />
          Clear
        </Button>
      )}
    </div>
  );
}
